import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardList, CheckCircle, DollarSign, TrendingUp, Sparkles, ArrowRight } from "lucide-react";
import { Badge } from "../ui/badge";

const phases = [
  {
    name: "Phase 1 - Urgent",
    procedures: [
      { code: "D2750", desc: "Crown - Porcelain/Metal", tooth: "#14", fee: 1250, ins: 625 },
      { code: "D4341", desc: "Perio Scaling (per quad)", tooth: "UR", fee: 250, ins: 175 },
    ],
  },
  {
    name: "Phase 2 - Restorative",
    procedures: [
      { code: "D2392", desc: "Composite 2-Surface", tooth: "#19", fee: 285, ins: 200 },
      { code: "D2740", desc: "Crown - Porcelain", tooth: "#30", fee: 1350, ins: 675 },
    ],
  },
  {
    name: "Phase 3 - Cosmetic",
    procedures: [
      { code: "D2962", desc: "Labial Veneer", tooth: "#8", fee: 1500, ins: 0 },
      { code: "D2962", desc: "Labial Veneer", tooth: "#9", fee: 1500, ins: 0 },
    ],
  },
];

const PMSTreatmentPlanningSlide = () => {
  const [activePhase, setActivePhase] = useState(0);
  const [acceptedProcs, setAcceptedProcs] = useState(new Set());

  useEffect(() => {
    const cycle = async () => {
      while (true) {
        setActivePhase(0);
        setAcceptedProcs(new Set());
        await new Promise((r) => setTimeout(r, 2000));
        setAcceptedProcs(new Set([0]));
        await new Promise((r) => setTimeout(r, 1500));
        setAcceptedProcs(new Set([0, 1]));
        await new Promise((r) => setTimeout(r, 2000));
        setActivePhase(1);
        await new Promise((r) => setTimeout(r, 1500));
        setAcceptedProcs(new Set([0, 1, 2]));
        await new Promise((r) => setTimeout(r, 1500));
        setAcceptedProcs(new Set([0, 1, 2, 3]));
        await new Promise((r) => setTimeout(r, 2000));
        setActivePhase(2);
        await new Promise((r) => setTimeout(r, 3000));
      }
    };
    cycle();
  }, []);

  const totalFee = phases.flatMap((p) => p.procedures).reduce((s, p) => s + p.fee, 0);
  const totalIns = phases.flatMap((p) => p.procedures).reduce((s, p) => s + p.ins, 0);

  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-12 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-blue-100 rounded-full blur-[100px] opacity-60" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[600px] h-[600px] bg-indigo-100 rounded-full blur-[100px] opacity-60" />
      </div>

      <div className="flex flex-row h-full w-full max-w-[1600px] gap-10 relative z-10">
        {/* Left Side */}
        <div className="w-[38%] flex flex-col justify-center space-y-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold border border-blue-200 mb-4">
              <ClipboardList className="w-4 h-4" />
              <span>PMS Feature</span>
            </div>

            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-4">
              Comprehensive{" "}
              <span className="text-blue-600">Treatment Planning</span>
            </h2>

            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Create multi-phase treatment plans with automatic insurance
              estimates, patient presentation tools, and real-time acceptance
              tracking. Present plans patients can understand and accept.
            </p>
          </motion.div>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              "Multi-Phase",
              "Auto Insurance Calc",
              "Visual Presentation",
              "Acceptance Tracking",
              "Alt Options",
              "Sequencing",
            ].map((f, i) => (
              <motion.div
                key={f}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + i * 0.08, duration: 0.4 }}
                className="px-3 py-1.5 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200 shadow-sm"
              >
                {f}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Side: Treatment Plan Mockup */}
        <div className="w-[62%] h-full flex items-center justify-center relative">
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col h-[600px] relative z-10"
          >
            {/* Header */}
            <div className="h-14 border-b border-slate-100 flex items-center justify-between px-6 bg-slate-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                  <ClipboardList className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-sm">Treatment Plan</h3>
                  <p className="text-xs text-slate-500">Michael Rodriguez &bull; Created Feb 15, 2026</p>
                </div>
              </div>
              <Badge className="bg-blue-600 text-white hover:bg-blue-700 border-0">
                3 Phases
              </Badge>
            </div>

            {/* Phase Tabs */}
            <div className="flex gap-2 px-6 py-3 border-b border-slate-100">
              {phases.map((phase, i) => (
                <motion.button
                  key={i}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                    activePhase === i
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  animate={{
                    scale: activePhase === i ? 1.05 : 1,
                  }}
                >
                  {phase.name}
                </motion.button>
              ))}
            </div>

            {/* Procedures */}
            <div className="flex-1 p-6 overflow-y-auto space-y-3">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePhase}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-3"
                >
                  {phases[activePhase].procedures.map((proc, i) => {
                    const globalIdx = phases.slice(0, activePhase).reduce((s, p) => s + p.procedures.length, 0) + i;
                    const isAccepted = acceptedProcs.has(globalIdx);

                    return (
                      <motion.div
                        key={`${activePhase}-${i}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.15, duration: 0.4 }}
                        className={`rounded-xl p-4 border-2 transition-all duration-500 ${
                          isAccepted
                            ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                            : "bg-slate-50 border-slate-200 hover:border-blue-200"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${
                              isAccepted ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-600"
                            }`}>
                              {proc.tooth}
                            </div>
                            <div>
                              <span className="text-sm font-bold text-blue-600">{proc.code}</span>
                              <p className="text-sm text-slate-700 font-medium">{proc.desc}</p>
                            </div>
                          </div>
                          <AnimatePresence>
                            {isAccepted && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-lg text-xs font-bold"
                              >
                                <CheckCircle className="w-3 h-3" />
                                Accepted
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                          <div className="flex gap-6">
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase font-bold">Fee</p>
                              <p className="text-sm font-bold text-slate-900">${proc.fee.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase font-bold">Insurance</p>
                              <p className="text-sm font-bold text-blue-600">${proc.ins.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-slate-500 uppercase font-bold">Patient</p>
                              <p className="text-sm font-bold text-slate-900">
                                ${(proc.fee - proc.ins).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Total Plan Value Card */}
            <div className="mx-6 mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-4 text-white shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium opacity-80 mb-0.5">Total Plan Value</p>
                  <span className="text-3xl font-extrabold">${totalFee.toLocaleString()}</span>
                </div>
                <div className="flex gap-5 text-sm">
                  <div className="text-right">
                    <p className="text-[10px] opacity-70 uppercase font-bold tracking-wider">Insurance</p>
                    <p className="font-bold text-lg">${totalIns.toLocaleString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] opacity-70 uppercase font-bold tracking-wider">Patient</p>
                    <p className="font-bold text-lg">${(totalFee - totalIns).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Summary */}
            <div className="h-16 border-t border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50 flex items-center justify-between px-6">
              <div className="flex items-center gap-3">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-bold text-blue-900">
                  Case Acceptance: {acceptedProcs.size}/{phases.flatMap(p => p.procedures).length} procedures
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-bold text-blue-600">AI-Assisted Estimates</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PMSTreatmentPlanningSlide;
